import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [amount, setAmount] = useState(0);
  const { clear } = useCart();
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 10) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setAmount(r.data.amount);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="pt-32 pb-24 mx-auto max-w-[900px] px-6 text-center" data-testid="checkout-success-page">
      {status === "polling" && (
        <>
          <Loader2 size={32} strokeWidth={1} className="animate-spin mx-auto text-muted-foreground" />
          <h1 className="serif text-4xl mt-8">Confirming your order…</h1>
          <p className="text-muted-foreground mt-4">Please wait while we confirm your payment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={40} strokeWidth={1} className="mx-auto text-foreground" />
          <div className="overline text-muted-foreground mt-8">Confirmed</div>
          <h1 className="serif text-5xl md:text-6xl mt-4">Thank you.</h1>
          <p className="text-muted-foreground mt-6 max-w-lg mx-auto">
            Your order has been received. A confirmation will be emailed shortly. Your pieces will be dispatched within two business days.
          </p>
          <div className="mt-6 text-lg serif">Total paid: ${amount.toFixed(2)}</div>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/shop" className="btn-sharp primary">Continue shopping</Link>
            <Link to="/" className="btn-sharp ghost">Return home</Link>
          </div>
        </>
      )}
      {(status === "error" || status === "expired" || status === "timeout") && (
        <>
          <h1 className="serif text-4xl">Something went wrong.</h1>
          <p className="text-muted-foreground mt-4">
            {status === "expired" ? "Your session expired." : "We couldn't confirm your payment. Please contact us if you were charged."}
          </p>
          <Link to="/cart" className="btn-sharp primary mt-8 inline-block">Back to cart</Link>
        </>
      )}
    </div>
  );
}
